import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';

import { useTimezone } from '@/components/contexts/TimezoneContext';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/NewButton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const UTC_LABEL = 'UTC (Coordinated Universal Time)';

function formatTimezone(tz: string) {
  return tz.replaceAll('_', ' ').replaceAll('/', ' / ');
}

export function timezoneLabel(tz: string) {
  return tz === 'UTC' ? UTC_LABEL : formatTimezone(tz);
}

/**
 * Searchable timezone picker, grouped by region. Reads and writes the timezone
 * on {@link useTimezone}, so it needs no props beyond styling.
 */
export function TimezoneCombobox({
  className,
  contentClassName,
  align = 'start',
}: {
  className?: string;
  contentClassName?: string;
  align?: 'start' | 'center' | 'end';
}) {
  const { t } = useTranslation('common');
  const { timezone, setTimezone, availableTimezones } = useTimezone();
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const groupedTimezones = useMemo(() => {
    const filtered = searchQuery
      ? availableTimezones.filter((tz) => tz.toLowerCase().includes(searchQuery.toLowerCase()))
      : availableTimezones;
    const grouped: Record<string, string[]> = {};
    filtered.forEach((tz) => {
      const region = tz.split('/')[0];
      if (!grouped[region]) grouped[region] = [];
      grouped[region].push(tz);
    });
    return grouped;
  }, [availableTimezones, searchQuery]);

  const handleSelect = (value: string) => {
    setTimezone(value);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{timezoneLabel(timezone)}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[320px] p-0', contentClassName)} align={align}>
        <Command>
          <CommandInput
            placeholder={t('search-timezones-placeholder')}
            value={searchQuery}
            onValueChange={setSearchQuery}
            ref={inputRef}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>No timezone found</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="UTC"
                onSelect={() => handleSelect('UTC')}
                className="cursor-pointer"
              >
                <Check
                  className={cn('mr-2 h-4 w-4', timezone === 'UTC' ? 'opacity-100' : 'opacity-0')}
                />
                {UTC_LABEL}
              </CommandItem>
            </CommandGroup>
            {Object.keys(groupedTimezones)
              .sort()
              .map((region) => (
                <CommandGroup key={region} heading={region}>
                  {groupedTimezones[region].sort().map((tz) => (
                    <CommandItem
                      key={tz}
                      value={tz}
                      onSelect={() => handleSelect(tz)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4', timezone === tz ? 'opacity-100' : 'opacity-0')}
                      />
                      {formatTimezone(tz)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
